/**
 * SIMPLE RECIPE SERVICE v2.1
 * * Servicio para manejar recetas simplificadas
 * (basadas en componentes)
 * ✅ Agregada función loadSampleRecipes para carga segura sin duplicados
 */

import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  setDoc, 
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  query,
  where
} from 'firebase/firestore';

// ✅ IMPORTANTE: Necesitamos importar las recetas de ejemplo
import { SAMPLE_RECIPES } from '../models/simpleRecipes';

// ============================================
// CREAR RECETA
// ============================================

export const createRecipe = async (householdId, recipeData) => {
  try {
    // Generar ID único
    const recipeId = `recipe_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Preparar datos
    const recipe = {
      ...recipeData,
      id: recipeId,
      householdId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Guardar en Firestore
    const recipeRef = doc(db, 'households', householdId, 'recipes', recipeId);
    await setDoc(recipeRef, recipe);

    console.log(`✅ Receta creada: ${recipe.name}`);

    return {
      success: true,
      recipe: {
        ...recipe,
        createdAt: new Date(),
        updatedAt: new Date()
      }
    };
  } catch (error) {
    console.error('❌ Error creando receta:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// ============================================
// OBTENER RECETA
// ============================================

export const getRecipe = async (householdId, recipeId) => {
  try {
    const recipeRef = doc(db, 'households', householdId, 'recipes', recipeId);
    const recipeSnap = await getDoc(recipeRef);

    if (!recipeSnap.exists()) {
      return {
        success: false,
        error: 'Receta no encontrada'
      };
    }

    const data = recipeSnap.data();
    const recipe = {
      id: recipeSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date()
    };

    return {
      success: true,
      recipe
    };
  } catch (error) {
    console.error('❌ Error obteniendo receta:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// ============================================
// OBTENER TODAS LAS RECETAS
// ============================================

export const getAllRecipes = async (householdId) => {
  try {
    const recipesRef = collection(db, 'households', householdId, 'recipes');
    const recipesSnap = await getDocs(recipesRef);

    const recipes = recipesSnap.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date()
      };
    });

    return {
      success: true,
      recipes
    };
  } catch (error) {
    console.error('❌ Error obteniendo recetas:', error);
    return {
      success: false,
      error: error.message,
      recipes: []
    };
  }
};

// ============================================
// ACTUALIZAR RECETA
// ============================================

export const updateRecipe = async (householdId, recipeId, updates) => {
  try {
    const recipeRef = doc(db, 'households', householdId, 'recipes', recipeId);
    
    await updateDoc(recipeRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });

    console.log(`✅ Receta actualizada: ${recipeId}`);

    return {
      success: true,
      recipe: {
        id: recipeId,
        ...updates,
        updatedAt: new Date()
      }
    };
  } catch (error) {
    console.error('❌ Error actualizando receta:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// ============================================
// ELIMINAR RECETA
// ============================================

export const deleteRecipe = async (householdId, recipeId) => {
  try {
    const recipeRef = doc(db, 'households', householdId, 'recipes', recipeId);
    await deleteDoc(recipeRef);

    console.log(`✅ Receta eliminada: ${recipeId}`);

    return {
      success: true
    };
  } catch (error) {
    console.error('❌ Error eliminando receta:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// ============================================
// SUSCRIBIRSE A RECETAS (TIEMPO REAL)
// ============================================

export const subscribeToRecipes = (householdId, callback) => {
  try {
    const recipesRef = collection(db, 'households', householdId, 'recipes');
    
    const unsubscribe = onSnapshot(
      recipesRef,
      (snapshot) => {
        const recipes = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date()
          };
        });

        console.log(`🔄 Recetas actualizadas: ${recipes.length}`);
        callback(recipes);
      },
      (error) => {
        console.error('❌ Error en suscripción a recetas:', error);
        callback([]);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('❌ Error suscribiendo a recetas:', error);
    return () => {};
  }
};

// ============================================
// HELPERS
// ============================================

/**
 * Expandir ingredientes de una receta basada en componentes
 * (calcula ingredientes finales multiplicando por servings)
 */
export const getExpandedIngredients = (recipe, components) => {
  if (!recipe || !recipe.main) return [];

  const expandedIngredients = [];

  // Función helper para expandir un componente
  const expandComponent = (componentId, servings) => {
    const component = components.find(c => c.id === componentId);
    if (!component) return;

    component.ingredients.forEach(ing => {
      const key = `${ing.name}_${ing.unit}`;
      const existing = expandedIngredients.find(
        e => `${e.name}_${e.unit}` === key
      );

      const quantity = ing.quantity * servings;

      if (existing) {
        existing.quantity += quantity;
      } else {
        expandedIngredients.push({
          name: ing.name,
          quantity,
          unit: ing.unit,
          category: ing.category
        });
      }
    });
  };

  // Expandir componente principal
  // `recipe.main` puede venir como objeto ({ componentId, servings }) o como id legacy.
  const mainComponentId = typeof recipe.main === 'object' ? recipe.main.componentId : recipe.main;
  const mainServings = typeof recipe.main === 'object'
    ? (recipe.main.servings || recipe.servings || 1)
    : (recipe.servings || 1);
  if (mainComponentId) {
    expandComponent(mainComponentId, mainServings);
  }

  // Expandir sides
  if (recipe.sides && recipe.sides.length > 0) {
    recipe.sides.forEach(side => {
      expandComponent(side.componentId, side.servings);
    });
  }

  return expandedIngredients;
};

/**
 * Recalcular todas las recetas de un household
 * (útil cuando se actualizan componentes)
 */
export const recalculateAllRecipes = async (householdId, components) => {
  try {
    const result = await getAllRecipes(householdId);
    if (!result.success) {
      return result;
    }

    const recipes = result.recipes;
    let updated = 0;

    for (const recipe of recipes) {
      const newIngredients = getExpandedIngredients(recipe, components);
      
      // Solo actualizar si cambió
      const changed = JSON.stringify(recipe.expandedIngredients) !== JSON.stringify(newIngredients);
      
      if (changed) {
        await updateRecipe(householdId, recipe.id, {
          expandedIngredients: newIngredients
        });
        updated++;
      }
    }

    console.log(`✅ ${updated} recetas recalculadas`);

    return {
      success: true,
      updated
    };
  } catch (error) {
    console.error('❌ Error recalculando recetas:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// ============================================
// ✅ CARGAR RECETAS DE EJEMPLO (SIN DUPLICADOS)
// ============================================
export const loadSampleRecipes = async (householdId) => {
  try {
    console.log(`📦 Cargando ejemplos de ${SAMPLE_RECIPES.length} recetas...`);

    // 1️⃣ Obtener recetas existentes
    const existingRecipesResult = await getAllRecipes(householdId);
    if (!existingRecipesResult.success) throw new Error(existingRecipesResult.error);
    
    const existingNames = new Set(
      existingRecipesResult.recipes.map(r => r.name.toLowerCase().trim())
    );

    // 2️⃣ Filtrar solo las que NO existen
    const newRecipes = SAMPLE_RECIPES.filter(sample => {
      const normalizedName = sample.name.toLowerCase().trim();
      return !existingNames.has(normalizedName);
    });

    if (newRecipes.length === 0) {
      console.log('✓ Todas las recetas de ejemplo ya están cargadas');
      return { success: true, loaded: 0, skipped: SAMPLE_RECIPES.length, total: SAMPLE_RECIPES.length };
    }

    // 3️⃣ Crear solo las nuevas
    let loaded = 0;
    let errors = 0;

    for (const recipe of newRecipes) {
      const result = await createRecipe(householdId, {
        name: recipe.name,
        type: recipe.type,
        category: recipe.category,
        servings: recipe.servings,
        main: recipe.main,
        sides: recipe.sides || [],
        instructions: recipe.instructions || '',
        prepTime: recipe.prepTime || 0,
        difficulty: recipe.difficulty || 'fácil'
      });

      if (result.success) loaded++;
      else errors++;
    }

    console.log(`✅ Recetas cargadas: ${loaded}`);
    return { success: true, loaded, skipped: SAMPLE_RECIPES.length - newRecipes.length, errors, total: SAMPLE_RECIPES.length };
  } catch (error) {
    console.error('❌ Error cargando recetas base:', error);
    return { success: false, error: error.message, loaded: 0, skipped: 0 };
  }
};

// ============================================
// EXPORTAR COMO DEFAULT
// ============================================

export default {
  createRecipe,
  getRecipe,
  getAllRecipes,
  updateRecipe,
  deleteRecipe,
  subscribeToRecipes,
  getExpandedIngredients,
  recalculateAllRecipes,
  loadSampleRecipes // ✅ Nueva función agregada a la exportación
};